import { inject, Injectable } from '@angular/core';
import {
  ClientCreditNoteFeeService,
  ClientOrderFeeService,
  ClientQuoteFeeService,
  ClientQuoteItemService,
  ClientQuoteService,
  ClientQuoteTotalsService,
  ClientReturnItemService,
  ClientReturnService,
  RouteEntitySubscriberService,
  SupplierCreditNoteItemMappingsService,
  SupplierCreditNoteItemService,
  SupplierCreditNoteService,
  SupplierInvoiceSectionService,
  SupplierOrderFeeService,
} from '../../services';
// @ts-ignore
import { environment } from '@environments';
import { forkJoin, Observable, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ClientCreditNote,
  ClientCreditNoteDraftItem,
  ClientCreditNoteFee,
  ClientCreditNoteItem,
  ClientCreditNoteTotals,
  ClientInvoice,
  ClientInvoiceDraftItem,
  ClientInvoiceFee,
  ClientInvoiceItem,
  ClientInvoiceTotals,
  ClientOrder,
  ClientOrderFee,
  ClientOrderItem,
  ClientOrderTotals,
  ClientQuote,
  ClientQuoteFee,
  ClientQuoteItem,
  ClientQuoteTotals,
  ClientReturn,
  ClientReturnItem,
  DocumentMatchingFetchResult,
  DocumentSingleFetchResult,
  DocumentSingleMatchingFetchResult,
  Entity,
  EntityTrackingConfig,
  SupplierCreditNote,
  SupplierCreditNoteItem,
  SupplierCreditNoteItemMapping,
  SupplierInvoice,
  SupplierInvoiceItem,
  SupplierInvoiceItemMapping,
  SupplierInvoiceSection,
  SupplierOrder,
  SupplierOrderItem,
  SupplierOrderTotals,
} from '../../models';
import {
  ClientCreditNoteDraftItemService,
  ClientCreditNoteItemService,
  ClientCreditNoteService,
  ClientCreditNoteTotalsService,
  ClientInvoiceDraftItemService,
  ClientInvoiceFeeService,
  ClientInvoiceItemService,
  ClientInvoiceService,
  ClientInvoiceTotalsService,
  ClientOrderItemService,
  ClientOrderService,
  ClientOrderTotalsService,
  SupplierInvoiceItemMappingService,
  SupplierInvoiceItemService,
  SupplierInvoiceService,
  SupplierOrderItemService,
  SupplierOrderService,
  SupplierOrderTotalsService,
} from '../features';
import { TrackingTypes } from '../../enums';

@Injectable({ providedIn: 'root' })
export class RouteEntitySubscriberSetup {
  private readonly clientOrderService = inject(ClientOrderService);
  private readonly clientQuoteFeeService = inject(ClientQuoteFeeService);
  private readonly clientOrderFeeService = inject(ClientOrderFeeService);
  private readonly supplierOrderService = inject(SupplierOrderService);
  private readonly supplierOrderItemService = inject(SupplierOrderItemService);
  private readonly clientOrderItemService = inject(ClientOrderItemService);
  private readonly supplierOrderFeeService = inject(SupplierOrderFeeService);
  private readonly supplierOrderTotalsService = inject(SupplierOrderTotalsService);
  private readonly clientCreditNoteService = inject(ClientCreditNoteService);
  private readonly clientCreditNoteItemService = inject(ClientCreditNoteItemService);
  private readonly clientCreditNoteTotalsService = inject(ClientCreditNoteTotalsService);
  private readonly clientCreditNoteDraftItemService = inject(ClientCreditNoteDraftItemService);
  private readonly supplierCreditNoteService = inject(SupplierCreditNoteService);
  private readonly clientCreditNoteFeeService = inject(ClientCreditNoteFeeService);
  private readonly supplierCreditNoteItemService = inject(SupplierCreditNoteItemService);
  private readonly supplierCreditNoteItemMappingsService = inject(SupplierCreditNoteItemMappingsService);
  private readonly clientOrderTotalsService = inject(ClientOrderTotalsService);
  private readonly routeEntitySubscriberService = inject(RouteEntitySubscriberService);
  private readonly supplierInvoiceService = inject(SupplierInvoiceService);
  private readonly supplierInvoiceItemService = inject(SupplierInvoiceItemService);
  private readonly supplierInvoiceItemMappingService = inject(SupplierInvoiceItemMappingService);
  private readonly clientInvoiceDraftItemService = inject(ClientInvoiceDraftItemService);
  private readonly clientInvoiceService = inject(ClientInvoiceService);
  private readonly clientInvoiceItemService = inject(ClientInvoiceItemService);
  private readonly clientInvoiceTotalsService = inject(ClientInvoiceTotalsService);
  private readonly clientInvoiceFeeService = inject(ClientInvoiceFeeService);
  private readonly supplierInvoiceSectionService = inject(SupplierInvoiceSectionService);
  private readonly clientQuoteService = inject(ClientQuoteService);
  private readonly clientQuoteItemService = inject(ClientQuoteItemService);
  private readonly clientQuoteTotalsService = inject(ClientQuoteTotalsService);
  private readonly clientReturnService = inject(ClientReturnService);
  private readonly clientReturnItemService = inject(ClientReturnItemService);

  private readonly orderTrackingBlueprints: EntityTrackingConfig[];

  constructor() {
    const clientCreditNoteItemService = this.clientCreditNoteItemService;
    const clientCreditNoteDraftItemService = this.clientCreditNoteDraftItemService;
    const supplierCreditNoteItemService = this.supplierCreditNoteItemService;
    const supplierCreditNoteItemMappingsService = this.supplierCreditNoteItemMappingsService;

    this.orderTrackingBlueprints = [
      {
        paramKey: 'clientOrderId',
        signalKey: 'clientOrder',
        listRoute: '/sales/orders/client_orders',
        releaseUrl: (e) => `${environment.apiUrl}/private${e['@id']}/unsubscribe`,
        translationsKey: 'CLIENT_ORDER',
        fetch: (
          id: string,
          entity?: Entity,
        ): Observable<DocumentSingleFetchResult<ClientOrder, ClientOrderItem, ClientOrderTotals, ClientOrderFee>> => {
          const iri = `/client_orders/${id}`;
          if (entity) {
            return forkJoin({
              entity: of(entity as ClientOrder),
              items: this.clientOrderItemService.getClientOrderItemsForClientOrder(iri),
              totals: this.clientOrderTotalsService.getClientOrderTotalsForOrder(iri),
              fees: this.clientOrderFeeService.getClientOrderFeesForClientOrder(iri),
            }).pipe(
              map((res) => ({
                type: TrackingTypes.SINGLE,
                entity: res.entity,
                items: res.items,
                totals: res.totals,
                fees: res.fees,
              })),
            );
          }
          return forkJoin({
            entity: this.clientOrderService.getClientOrderById(id),
            items: this.clientOrderItemService.getClientOrderItemsForClientOrder(iri),
            totals: this.clientOrderTotalsService.getClientOrderTotalsForOrder(iri),
            fees: this.clientOrderFeeService.getClientOrderFeesForClientOrder(iri),
          }).pipe(
            // remove the extra `of(...)` you had here
            map(
              (res) =>
                ({
                  type: TrackingTypes.SINGLE,
                  entity: res.entity,
                  items: res.items,
                  totals: res.totals,
                  fees: res.fees,
                }) as DocumentSingleFetchResult<ClientOrder, ClientOrderItem, ClientOrderTotals, ClientOrderFee>,
            ),
          );
        },
        updateUsage: (id, e) => this.clientOrderService.updateClientOrder(id, e as ClientOrder),
      },
      {
        paramKey: 'clientQuoteId',
        signalKey: 'clientQuote',
        listRoute: '/sales/client_quotes',
        releaseUrl: (e) => `${environment.apiUrl}/private${e['@id']}/unsubscribe`,
        translationsKey: 'CLIENT_QUOTE',
        fetch: (
          id: string,
          entity?: Entity,
        ): Observable<DocumentSingleFetchResult<ClientQuote, ClientQuoteItem, ClientQuoteTotals, ClientQuoteFee>> => {
          const iri = `/client_quotes/${id}`;
          if (entity) {
            return forkJoin({
              entity: of(entity as ClientQuote),
              items: this.clientQuoteItemService.getClientQuoteItemsForClientQuote(iri),
              totals: this.clientQuoteTotalsService.getClientQuoteTotalsForClientQuote(iri),
              fees: this.clientQuoteFeeService.getClientQuoteFeesForClientQuote(iri),
            }).pipe(
              map((res) => ({
                type: TrackingTypes.SINGLE,
                entity: res.entity,
                items: res.items,
                totals: res.totals,
                fees: res.fees,
              })),
            );
          }
          return forkJoin({
            entity: this.clientQuoteService.getClientQuoteByIri(iri),
            items: this.clientQuoteItemService.getClientQuoteItemsForClientQuote(iri),
            totals: this.clientQuoteTotalsService.getClientQuoteTotalsForClientQuote(iri),
            fees: this.clientQuoteFeeService.getClientQuoteFeesForClientQuote(iri),
          }).pipe(
            map(
              (res) =>
                ({
                  type: TrackingTypes.SINGLE,
                  entity: res.entity,
                  items: res.items,
                  totals: res.totals,
                  fees: res.fees,
                }) as DocumentSingleFetchResult<ClientQuote, ClientQuoteItem, ClientQuoteTotals, ClientQuoteFee>,
            ),
          );
        },
        updateUsage: (id, e) => this.clientQuoteService.update(id, e as ClientQuote),
      },
      {
        paramKey: 'supplierOrderId',
        signalKey: 'supplierOrder',
        listRoute: '/purchase/supplier_orders',
        releaseUrl: (e) => `${environment.apiUrl}/private${e['@id']}/unsubscribe`,
        translationsKey: 'SUPPLIER_ORDER',
        fetch: (
          id: string,
          entity?: Entity,
        ): Observable<DocumentSingleFetchResult<SupplierOrder, SupplierOrderItem, SupplierOrderTotals>> => {
          const iri = `/supplier_orders/${id}`;

          if (entity) {
            return forkJoin({
              entity: of(entity as SupplierOrder),
              items: this.supplierOrderItemService
                .getSupplierOrderItemsForOrderByIri(iri)
                .pipe(map((r) => r['hydra:member'])),
              totals: this.supplierOrderTotalsService.getSupplierOrderTotalsForOrder(iri),
            }).pipe(
              map(
                (res) =>
                  ({
                    type: TrackingTypes.SINGLE,
                    entity: res.entity,
                    items: res.items,
                    totals: res.totals,
                  }) as DocumentSingleFetchResult<SupplierOrder, SupplierOrderItem, SupplierOrderTotals>,
              ),
            );
          } else {
            return forkJoin({
              entity: this.supplierOrderService.getSupplierOrderById(id),
              items: this.supplierOrderItemService
                .getSupplierOrderItemsForOrderByIri(iri)
                .pipe(map((r) => r['hydra:member'])),
              totals: this.supplierOrderTotalsService.getSupplierOrderTotalsForOrder(iri),
            }).pipe(
              map(
                (res) =>
                  ({
                    type: TrackingTypes.SINGLE,
                    entity: res.entity,
                    items: res.items,
                    totals: res.totals,
                  }) as DocumentSingleFetchResult<SupplierOrder, SupplierOrderItem, SupplierOrderTotals>,
              ),
            );
          }
        },
        updateUsage: (id, e) => this.supplierOrderService.updateSupplierOrder(id, e as SupplierOrder),
      },
      // {
      //   paramKey: 'supplierInvoiceId',
      //   signalKey: 'supplierInvoice',
      //   listRoute: '/purchase/supplier_invoices',
      //   releaseUrl: (e) => `${environment.apiUrl}/private${e['@id']}/unsubscribe`,
      //   translationsKey: 'SUPPLIER_INVOICE',
      //   fetch: (
      //     id: string,
      //     entity?: Entity,
      //   ): Observable<
      //     DocumentMatchingFetchResult<
      //       SupplierInvoice,
      //       SupplierInvoiceItem,
      //       ClientOrderItem,
      //       SupplierInvoiceItemMapping,
      //       ClientInvoiceDraftItem,
      //       SupplierInvoiceSection
      //     >
      //   > => {
      //     const iri = `/supplier_invoices/${id}`;
      //
      //     // Always use the same entry point
      //     const entity$ = entity ? of(entity as SupplierInvoice) : this.supplierInvoiceService.get(iri);
      //
      //     return entity$.pipe(
      //       switchMap((invoice: SupplierInvoice) =>
      //         forkJoin({
      //           entity: of(invoice),
      //           items: this.supplierInvoiceItemService.getForSupplierInvoiceSection(invoice.supplierInvoiceSections),
      //           linkedItems: this.clientOrderItemService.getForSupplierInvoiceSection(
      //             invoice.supplierInvoiceSections,
      //           ),
      //           itemMappings: this.supplierInvoiceItemMappingService.getForSupplierInvoiceSection(
      //             invoice.supplierInvoiceSections,
      //           ),
      //           linkedDraftItems: this.clientInvoiceDraftItemService.getForSupplierInvoiceSection(
      //             invoice.supplierInvoiceSections,
      //           ),
      //           sections: this.supplierInvoiceSectionService.getForSupplierInvoice(invoice['@id']!),
      //         }),
      //       ),
      //       map(
      //         (res) =>
      //           ({
      //             type: TrackingTypes.MATCHING,
      //             entity: res.entity,
      //             items: res.items,
      //             linkedItems: res.linkedItems,
      //             itemMappings: res.itemMappings,
      //             draftItems: res.linkedDraftItems,
      //             sections: res.sections,
      //           }) as DocumentMatchingFetchResult<
      //             SupplierInvoice,
      //             SupplierInvoiceItem,
      //             ClientOrderItem,
      //             SupplierInvoiceItemMapping,
      //             ClientInvoiceDraftItem,
      //             SupplierInvoiceSection
      //           >,
      //       ),
      //       // Optional: make safe for multiple subscribers (resolver + component)
      //       // and keep the latest value for late subscribers
      //       // shareReplay({ bufferSize: 1, refCount: false }),
      //     );
      //   },
      //   updateUsage: (id, e) => this.supplierInvoiceService.patch(id, e as SupplierInvoice),
      // },
      {
        paramKey: 'supplierInvoiceId',
        signalKey: 'supplierInvoice',
        listRoute: '/purchase/supplier_invoices',
        releaseUrl: (e) => `${environment.apiUrl}/private${e['@id']}/unsubscribe`,
        translationsKey: 'SUPPLIER_INVOICE',
        fetch: (
          id: string,
          entity?: Entity,
        ): Observable<
          DocumentMatchingFetchResult<
            SupplierInvoice,
            SupplierInvoiceItem,
            ClientOrderItem,
            SupplierInvoiceItemMapping,
            ClientInvoiceDraftItem,
            SupplierInvoiceSection
          >
        > => {
          const iri = `/supplier_invoices/${id}`;
          const entity$ = entity ? of(entity as SupplierInvoice) : this.supplierInvoiceService.get(iri);

          return entity$.pipe(
            switchMap((invoice: SupplierInvoice) =>
              this.supplierInvoiceSectionService.getForSupplierInvoice(invoice['@id']!).pipe(
                map(
                  (sections) =>
                    ({
                      type: TrackingTypes.MATCHING,
                      entity: invoice,
                      // these will be filled in later by the detail component
                      items: {},
                      linkedItems: {},
                      itemMappings: {},
                      draftItems: {},
                      sections,
                    }) as DocumentMatchingFetchResult<
                      SupplierInvoice,
                      SupplierInvoiceItem,
                      ClientOrderItem,
                      SupplierInvoiceItemMapping,
                      ClientInvoiceDraftItem,
                      SupplierInvoiceSection
                    >,
                ),
              ),
            ),
          );
        },
        updateUsage: (id, e) => this.supplierInvoiceService.patch(id, e as SupplierInvoice),
      },
      {
        paramKey: 'clientInvoiceId',
        signalKey: 'clientInvoice',
        listRoute: '/sales/client_invoices',
        releaseUrl: (e) => `${environment.apiUrl}/private${e['@id']}/unsubscribe`,
        translationsKey: 'CLIENT_INVOICE',
        fetch: (
          id: string,
          entity?: Entity,
        ): Observable<
          DocumentSingleFetchResult<ClientInvoice, ClientInvoiceItem, ClientInvoiceTotals, ClientInvoiceFee>
        > => {
          const iri = `/client_invoices/${id}`;

          const entity$ = entity ? of(entity as ClientInvoice) : this.clientInvoiceService.get(iri);

          return entity$.pipe(
            switchMap((invoice: ClientInvoice) =>
              forkJoin({
                entity: of(invoice),
                items: this.clientInvoiceItemService.getForClientInvoice(iri).pipe(map((r) => r)),
                totals: this.clientInvoiceTotalsService.getForClientInvoice(iri),
                fees: this.clientInvoiceFeeService.getClientInvoiceFeesForClientInvoice(iri),
              }),
            ),
            map(
              (res) =>
                ({
                  type: TrackingTypes.SINGLE,
                  entity: res.entity,
                  items: res.items,
                  totals: res.totals,
                  fees: res.fees,
                }) as unknown as DocumentSingleFetchResult<
                  ClientInvoice,
                  ClientInvoiceItem,
                  ClientInvoiceTotals,
                  ClientInvoiceFee
                >,
            ),
          );
        },
        updateUsage: (id, e) => this.clientInvoiceService.patch(id, e as ClientInvoice),
      },
      {
        paramKey: 'clientCreditNoteId',
        signalKey: 'clientCreditNote',
        listRoute: '/sales/client_credit_notes',
        releaseUrl: (e) => `${environment.apiUrl}/private${e['@id']}/unsubscribe`,
        translationsKey: 'CLIENT_CREDIT_NOTE',
        fetch: (
          id: string,
          entity?: Entity,
        ): Observable<
          DocumentSingleFetchResult<ClientCreditNote, ClientCreditNoteItem, ClientCreditNoteTotals, ClientCreditNoteFee>
        > => {
          const iri = `/client_credit_notes/${id}`;

          if (entity) {
            return forkJoin({
              entity: of(entity as ClientCreditNote),
              items: this.clientCreditNoteItemService.getForClientCreditNote(iri),
              totals: this.clientCreditNoteTotalsService.getForClientCreditNote(iri),
              fees: this.clientCreditNoteFeeService.geteFeesForClientCreditNote(iri),
            }).pipe(
              map(
                (res) =>
                  ({
                    type: TrackingTypes.SINGLE,
                    entity: res.entity,
                    items: res.items,
                    totals: res.totals,
                    fees: res.fees,
                  }) as DocumentSingleFetchResult<
                    ClientCreditNote,
                    ClientCreditNoteItem,
                    ClientCreditNoteTotals,
                    ClientCreditNoteFee
                  >,
              ),
            );
          } else {
            return forkJoin({
              entity: this.clientCreditNoteService.getById(id),
              items: this.clientCreditNoteItemService.getForClientCreditNote(iri),
              totals: this.clientCreditNoteTotalsService.getForClientCreditNote(iri),
              fees: this.clientCreditNoteFeeService.geteFeesForClientCreditNote(iri),
            }).pipe(
              map(
                (res) =>
                  ({
                    type: TrackingTypes.SINGLE,
                    entity: res.entity,
                    items: res.items,
                    totals: res.totals,
                    fees: res.fees,
                  }) as DocumentSingleFetchResult<
                    ClientCreditNote,
                    ClientCreditNoteItem,
                    ClientCreditNoteTotals,
                    ClientCreditNoteFee
                  >,
              ),
            );
          }
        },
        updateUsage: (id, e) => this.clientCreditNoteService.update(id, e as ClientCreditNote),
      },
      {
        paramKey: 'supplierCreditNoteId',
        signalKey: 'supplierCreditNote',
        listRoute: '/purchase/supplier_credit_notes',
        releaseUrl: (e) => `${environment.apiUrl}/private${e['@id']}/unsubscribe`,
        translationsKey: 'SUPPLIER_CREDIT_NOTE',
        fetch: (
          id: string,
          entity?: Entity,
        ): Observable<
          DocumentSingleMatchingFetchResult<
            SupplierCreditNote,
            SupplierCreditNoteItem,
            SupplierCreditNoteItemMapping,
            ClientCreditNoteDraftItem,
            ClientCreditNoteItem
          >
        > => {
          const iri = `/supplier_credit_notes/${id}`;

          if (entity) {
            return forkJoin({
              entity: of(entity as SupplierCreditNote),
              items: supplierCreditNoteItemService.getForSupplierCreditNote(iri),
              itemMappings: supplierCreditNoteItemMappingsService.getForSupplierCreditNote(iri),
              draftItems: clientCreditNoteDraftItemService.getForSupplierCreditNote(iri),
              linkedItems: clientCreditNoteItemService.getForSupplierCreditNote(iri),
            }).pipe(
              map(
                (res) =>
                  ({
                    type: TrackingTypes.SINGLE_MATCHING,
                    entity: res.entity,
                    items: res.items,
                    itemMappings: res.itemMappings,
                    draftItems: res.draftItems,
                    linkedItems: res.linkedItems,
                  }) as DocumentSingleMatchingFetchResult<
                    SupplierCreditNote,
                    SupplierCreditNoteItem,
                    SupplierCreditNoteItemMapping,
                    ClientCreditNoteDraftItem,
                    ClientCreditNoteItem
                  >,
              ),
            );
          } else {
            return forkJoin({
              entity: this.supplierCreditNoteService.get(iri),
              items: this.supplierCreditNoteItemService.getForSupplierCreditNote(iri),
              itemMappings: supplierCreditNoteItemMappingsService.getForSupplierCreditNote(iri),
              draftItems: clientCreditNoteDraftItemService.getForSupplierCreditNote(iri),
              linkedItems: clientCreditNoteItemService.getForSupplierCreditNote(iri),
              // totals: this.supplierCreditNoteTotalsService.getForSupplierCreditNote(iri),
            }).pipe(
              map(
                (res) =>
                  ({
                    type: TrackingTypes.SINGLE_MATCHING,
                    entity: res.entity,
                    items: res.items,
                    itemMappings: res.itemMappings,
                    draftItems: res.draftItems,
                    linkedItems: res.linkedItems,
                    // totals: res.totals,
                  }) as DocumentSingleMatchingFetchResult<
                    SupplierCreditNote,
                    SupplierCreditNoteItem,
                    SupplierCreditNoteItemMapping,
                    ClientCreditNoteDraftItem,
                    ClientCreditNoteItem
                  >,
              ),
            );
          }
        },
        updateUsage: (id, e) => this.supplierCreditNoteService.update(id, e as SupplierCreditNote),
      },
      {
        paramKey: 'clientReturnId',
        signalKey: 'clientReturn',
        listRoute: '/sales/client_returns',
        releaseUrl: (e) => `${environment.apiUrl}/private${e['@id']}/unsubscribe`,
        translationsKey: 'CLIENT_RETURN',
        fetch: (id: string, entity?: Entity): Observable<DocumentSingleFetchResult<ClientReturn, ClientReturnItem>> => {
          const iri = `/client_returns/${id}`;
          if (entity) {
            return forkJoin({
              entity: of(entity as ClientReturn),
              items: this.clientReturnItemService.getForClientReturn(iri),
            }).pipe(
              map((res) => ({
                type: TrackingTypes.SINGLE,
                entity: res.entity,
                items: res.items,
              })),
            );
          }
          return forkJoin({
            entity: this.clientReturnService.get(iri),
            items: this.clientReturnItemService.getForClientReturn(iri),
          }).pipe(
            map(
              (res) =>
                ({
                  type: TrackingTypes.SINGLE,
                  entity: res.entity,
                  items: res.items,
                }) as DocumentSingleFetchResult<ClientReturn, ClientReturnItem>,
            ),
          );
        },
        updateUsage: (id, e) => this.clientReturnService.update(id, e as ClientReturn),
      },
    ];

    for (const cfg of this.orderTrackingBlueprints) {
      this.routeEntitySubscriberService.trackEntity(cfg);
    }
  }

  trackEntities() {
    for (const cfg of this.orderTrackingBlueprints) {
      this.routeEntitySubscriberService.trackEntity(cfg);
    }
  }
}
